import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../store/features/notificationPreferencesApi";
import { registerServiceWorker, subscribeUser } from "../../../../utils/notification";
import SettingsContent from "../shared/SettingsContent";
import SettingsPageHeader from "../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../shared/SettingsPrimaryButton";
import BrowserNotificationBanner from "./BrowserNotificationBanner";
import {
  buildEventRows,
  DEFAULT_SOUND_OPTIONS,
  getErrorMessage,
  SOUND_URL,
} from "./constants";
import NotificationSettingsTable from "./NotificationSettingsTable";
import NotificationSoundCard from "./NotificationSoundCard";

function NotificationPreferencesPage() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.loginToken);
  const email = userData?.response?.user?.email;
  const businessId = userData?.response?.user?.business_id;

  const {
    loading,
    loaded,
    saving,
    triggers,
    triggerLabels,
    soundOptions,
    soundEnabled: storedSoundEnabled,
    soundKey: storedSoundKey,
    repeatSound: storedRepeatSound,
  } = useSelector((state) => state.notificationPreferences);

  const [preferences, setPreferences] = useState(null);
  const [soundKey, setSoundKey] = useState("default");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [repeatSound, setRepeatSound] = useState(false);
  const [browserPermission, setBrowserPermission] = useState(
    () => (typeof Notification !== "undefined" ? Notification.permission : "default"),
  );
  const [enablingBrowser, setEnablingBrowser] = useState(false);

  const audioRef = useRef(null);

  const browserNotificationsDisabled = useMemo(
    () => browserPermission !== "granted",
    [browserPermission],
  );

  const events = useMemo(
    () => buildEventRows(loaded ? triggerLabels : {}),
    [triggerLabels, loaded],
  );

  const resolvedSoundOptions = useMemo(
    () => (soundOptions?.length ? soundOptions : DEFAULT_SOUND_OPTIONS),
    [soundOptions],
  );

  useEffect(() => {
    if (!businessId) return;
    dispatch(fetchNotificationPreferences());
  }, [dispatch, businessId]);

  useEffect(() => {
    if (!loaded || !triggers) return;
    setPreferences(triggers);
    setSoundKey(storedSoundKey ?? "default");
    setSoundEnabled(storedSoundEnabled ?? true);
    setRepeatSound(storedRepeatSound ?? false);
  }, [loaded, triggers, storedSoundKey, storedSoundEnabled, storedRepeatSound]);

  const togglePreference = useCallback((eventKey, channel) => {
    setPreferences((prev) => ({
      ...prev,
      [eventKey]: {
        ...prev[eventKey],
        [channel]: !prev[eventKey]?.[channel],
      },
    }));
  }, []);

  const handleEnableBrowser = async () => {
    if (!("Notification" in window)) {
      toast.error("Browser notifications are not supported in this browser.");
      return;
    }

    setEnablingBrowser(true);
    try {
      const apiKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const registration = await registerServiceWorker();
      if (registration && apiKey) {
        await subscribeUser(registration, apiKey);
      } else {
        await Notification.requestPermission();
      }
      setBrowserPermission(Notification.permission);
      if (Notification.permission === "granted") {
        toast.success("Browser notifications enabled.");
      } else if (Notification.permission === "denied") {
        toast.error("Browser notifications were blocked. Enable them in your browser settings.");
      }
    } catch {
      toast.error("Could not enable browser notifications.");
    } finally {
      setEnablingBrowser(false);
    }
  };

  const handleTestSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(SOUND_URL);
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } catch {
      toast.error("Could not play notification sound.");
    }
  };

  const handleSave = async () => {
    if (!businessId || !preferences) return;

    const result = await dispatch(
      updateNotificationPreferences({
        triggers: preferences,
        soundEnabled,
        soundKey,
        repeatSound,
      }),
    );

    if (updateNotificationPreferences.fulfilled.match(result)) {
      window.dispatchEvent(
        new CustomEvent("sound-settings-updated", {
          detail: { enableNotifications: soundEnabled, repeatSound },
        }),
      );
      toast.success(result.payload?.message || "Preferences saved.");
    } else {
      toast.error(getErrorMessage(result.payload));
    }
  };

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Notification Preferences"
        description="Customize how and when you receive notifications"
      />

      {browserNotificationsDisabled ? (
        <BrowserNotificationBanner
          onEnable={handleEnableBrowser}
          enabling={enablingBrowser}
        />
      ) : null}

      <NotificationSettingsTable
        events={events}
        preferences={preferences || {}}
        onToggle={togglePreference}
        loading={!loaded || loading}
      />

      <NotificationSoundCard
        soundChoice={soundKey}
        soundOptions={resolvedSoundOptions}
        onSoundChange={setSoundKey}
        onTestSound={handleTestSound}
        disabled={!loaded || loading}
      />

      <SettingsPrimaryButton
        type="button"
        onClick={handleSave}
        disabled={saving || loading || !loaded || !preferences}
        sx={{ mt: "32px", width: 160 }}
      >
        {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save Preferences"}
      </SettingsPrimaryButton>
    </SettingsContent>
  );
}

export default NotificationPreferencesPage;
